import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  useTheme,
  Divider,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuizIcon from "@mui/icons-material/Quiz";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFinalAssessment } from "../api/courseAPI";

export default function FinalAssessment() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);

  const TOTAL_TIME_MINUTES = 30;

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const res = await getFinalAssessment(courseId);

        if (res.success && res.data?.length > 0) {
          setAssessment(res.data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch assessment", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [courseId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : "#F9FAFB",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 700,
          width: "100%",
          p: 4,
          borderRadius: 3,
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Typography variant="h5" fontWeight={600} mb={1}>
          Final Course Assessment
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Complete this assessment to successfully finish the course and unlock your certificate.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {loading && <Typography>Loading assessment...</Typography>}

        {!loading && assessment && (
          <>
            {/* Info Section */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
              <Chip
                icon={<QuizIcon />}
                label={`Total Questions: ${assessment.questions?.length || 0}`}
                variant="outlined"
              />

              <Chip
                icon={<AccessTimeIcon />}
                label={`Total Time: ${TOTAL_TIME_MINUTES} minutes`}
                variant="outlined"
              />

              {assessment.passMarks && (
                <Chip
                  label={`Passing Marks: ${assessment.passMarks}%`}
                  variant="outlined"
                />
              )}
            </Box>

            {/* Instructions Section */}
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.action.hover
                    : "#F4F7FF",
                p: 3,
                borderRadius: 2,
                mb: 4,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={1}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <InfoOutlinedIcon fontSize="small" />
                Instructions
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                • This assessment contains {assessment.questions?.length || 0} multiple-choice questions.
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                • You have {TOTAL_TIME_MINUTES} minutes to complete the assessment.
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                • Once started, the timer cannot be paused.
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                • Do not refresh or close the browser during the assessment.
              </Typography>

              <Typography variant="body2" color="text.secondary">
                • You must score at least {assessment.passMarks || 70}% to pass.
              </Typography>
            </Box>

            {/* Start Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ py: 1.5, fontWeight: 600 }}
              onClick={() =>
                navigate(`/course/${courseId}/final-assessment/start`)
              }
            >
              Start Assessment
            </Button>
          </>
        )}
      </Card>
    </Box>
  );
}